import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import React from "react";

import { useViewportStore } from "@agent-ui/features/page-renderer/store/viewportStore";
import { Button } from "@replo/design-system/components/button/Button";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import { Maximize, Monitor, Smartphone, Tablet } from "lucide-react";

type ViewportOption = "responsive" | EditorCanvas;

const VIEWPORT_OPTIONS: {
  value: ViewportOption;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
}[] = [
  {
    value: "responsive",
    icon: <Maximize size={18} />,
    label: "Responsive",
    tooltip: "Responsive (fill available space)",
  },
  {
    value: "mobile",
    icon: <Smartphone size={18} />,
    label: "Mobile",
    tooltip: `Mobile (${CANVAS_SIZES.mobile.defaultFrameWidth}px)`,
  },
  {
    value: "tablet",
    icon: <Tablet size={18} />,
    label: "Tablet",
    tooltip: `Tablet (${CANVAS_SIZES.tablet.defaultFrameWidth}px)`,
  },
  {
    value: "desktop",
    icon: <Monitor size={18} />,
    label: "Desktop",
    tooltip: `Desktop (${CANVAS_SIZES.desktop.defaultFrameWidth}px)`,
  },
];

const TRIGGER_ICONS: Record<ViewportOption, React.ReactNode> = {
  responsive: <Maximize />,
  mobile: <Smartphone />,
  tablet: <Tablet />,
  desktop: <Monitor />,
};

interface ResponsiveControlsProps {
  variant?: "primary" | "secondary" | "tertiary" | "destructive" | "link";
}

function ResponsiveControls({ variant = "tertiary" }: ResponsiveControlsProps) {
  const { currentViewport, viewportMode, applyPreset, setResponsive } =
    useViewportStore();
  const [open, setOpen] = React.useState(false);

  const activeValue: ViewportOption =
    viewportMode === "responsive" ? "responsive" : currentViewport;

  const handleSelect = (value: ViewportOption) => {
    if (value === "responsive") {
      setResponsive();
    } else {
      applyPreset(value);
    }
    setOpen(false);
  };

  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <Tooltip content="Viewport size" triggerAsChild>
        <PopoverTrigger render={<Button icon variant={variant} size="base" />}>
          {TRIGGER_ICONS[activeValue]}
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="center"
        sideOffset={4}
        showCloseButton={false}
        UNSAFE_className="w-auto p-1.5"
      >
        <Tabs
          value={activeValue}
          onValueChange={(value) => handleSelect(value as ViewportOption)}
        >
          <TabsList size="sm" variant="default">
            {VIEWPORT_OPTIONS.map((option) => (
              <Tooltip
                key={option.value}
                content={option.tooltip}
                triggerAsChild
              >
                <TabsTrigger
                  value={option.value}
                  size="sm"
                  aria-label={option.label}
                >
                  {option.icon}
                </TabsTrigger>
              </Tooltip>
            ))}
          </TabsList>
          {VIEWPORT_OPTIONS.map((option) => (
            <TabsContent key={option.value} value={option.value} hidden />
          ))}
        </Tabs>
      </PopoverContent>
    </PopoverRoot>
  );
}

export default ResponsiveControls;
